import React, { useCallback, useMemo, useState } from 'react';
import { useContext } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { borderRadiusValues } from 'shared/lib/utils/uiConstants';
import IconTrash from 'shared/icons/trash';
import IconEdit from 'shared/icons/iconEdit';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import UploadArea, {
  UploadedReadedFile,
} from 'shared/lib/components/controls/uploadArea/uploadArea';
import { isEqual } from 'lodash-es';
import {
  EditProfileData,
  ProfileModel,
} from 'entities/profile/model/userModel';
import { UploadedFileOrString } from 'shared/lib/utils/uploadedFileOrString';
import DesktopSmallModal from 'shared/lib/components/modals/desktopSmallModal';
import CroppModal from 'shared/lib/components/modals/croppModal';
import IconUndo from 'shared/icons/iconUndo';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import ProfileEditContainer from 'pages/profilePage/profileEdit/profileEditContainer';
import ProfileEditForm from 'pages/profilePage/forms/profileEditForm';
import IconFolder from 'shared/icons/iconFolder';
import ActionInlineButton from 'shared/lib/components/controls/buttons/actionInlineButton';
import PasswordEditForm from 'pages/profilePage/forms/passwordEditForm';
import { AxiosError } from 'axios';
import mobxStore from 'app/store/rootStore';

const PROFILE_EDIT_FORM_ID = 'profileEditForm';

export default function ProfileEdit({
  user,
  handleGoToProfileCard,
}: {
  user: ProfileModel;
  handleGoToProfileCard: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    mobxStore: { notificationsStore },
  } = useContext(MobxContext);
  const {
    spacings: {
      sp,
      profilePage: { profileEdit },
    },
  } = useContext(UiContext);

  const { isLessTablet, isLessLaptop } = useBooleanScreens();

  const [emailError, setEmailError] = useState<'emailAlreadyExists' | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currAvatar, setCurrAvatar] = useState(
    new UploadedFileOrString(user.avatar || '')
  );

  const [isPasswordEdit, setIsPasswordEdit] = useState(false);

  const [hasProfileEditFormChanged, setHasProfileEditFormChanged] =
    useState<boolean>(false);

  const [isAvatarUploadModalOpen, setIsAvatarUploadModalOpen] =
    useState<boolean>(false);
  const [isAvatarEditModalOpen, setIsAvatarEditModalOpen] =
    useState<boolean>(false);

  const onFileSelect = useCallback((image: UploadedReadedFile) => {
    setCurrAvatar(new UploadedFileOrString(image));
    setIsAvatarUploadModalOpen(false);
  }, []);

  const onFileCropp = useCallback((image: UploadedReadedFile) => {
    setCurrAvatar(new UploadedFileOrString(image));
    setIsAvatarEditModalOpen(false);
  }, []);

  const [formikSubmited, setFormikSubmited] = useState(false);

  const handleEditSubmit = useCallback(
    async (formValues: EditProfileData) => {
      try {
        const userData = await user.update(formValues);
        if (userData?.token?.access_token) {
          mobxStore.authStore.forceLogin(userData.token.access_token);
        }
      } catch (e) {
        const err = e as AxiosError;

        if (err.response?.status === 400) {
          setEmailError('emailAlreadyExists');
        }
        console.error(e);
        return;
      }

      try {
        const promises = [];
        if (currAvatar.file) {
          promises.push(user.updateAvatar({ avatar: currAvatar.file }));
        }

        if (currAvatar.src === '' && user.avatar !== '') {
          promises.push(user.deleteAvatar());
        }

        if (promises.length) {
          await Promise.all(promises);
        }

        handleGoToProfileCard();
      } catch (e) {
        console.error(e);
        notificationsStore.errorMessage = t('notifications.errors.default');
      }
    },
    [
      handleGoToProfileCard,
      t,
      notificationsStore,
      currAvatar.file,
      user,
      currAvatar.src,
    ]
  );

  const hasAvatarChanged = !isEqual(user.avatar, currAvatar.src);

  const applyButtons = (
    <ButtonsContainer
      sx={{ width: 1, mt: isLessTablet ? 8 : 0 }}
      variant={isLessLaptop ? 'vertical' : 'full'}
    >
      <SecondaryButton onClick={handleGoToProfileCard}>
        {t('common.cancel')}
      </SecondaryButton>
      <PrimaryButton
        disabled={!hasProfileEditFormChanged && !hasAvatarChanged}
        loading={isLoading}
        type="submit"
        form={PROFILE_EDIT_FORM_ID}
        onClick={() => setFormikSubmited(true)}
      >
        {t('common.save')}
      </PrimaryButton>
    </ButtonsContainer>
  );

  const uploadArea = useMemo(
    () => (
      <UploadArea
        isDataUrl
        triggerMode={isLessLaptop}
        accept={{
          'image/jpeg': ['.jpeg'],
          'image/jpg': ['.jpg'],
          'image/png': ['.png'],
        }}
        areMultipleUploadsAllowed={false}
        innerSx={{ height: 550 }}
        text={t('noCategory.uploadFileHelp')}
        buttonText={t('actions.filesUpload')}
        icon={
          <IconFolder sizeKey="48x48" extColor={theme.palette.text.secondary} />
        }
        handleAddFile={onFileSelect}
      ></UploadArea>
    ),
    [onFileSelect, theme.palette.text.secondary, t, isLessLaptop]
  );

  return (
    <ProfileEditContainer>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: isLessLaptop ? 1 : 'auto',
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex' }}>
          <img
            style={{
              width: profileEdit.avatarW,
              height: profileEdit.avatarH,
              minWidth: 200,
              minHeight: 200,
              borderRadius: theme.spacing(borderRadiusValues.round),
              cursor: 'pointer',
              objectFit: 'cover',
            }}
            alt="avatar"
            src={currAvatar.src || user.avatarPlaceholder}
            onClick={() => setIsAvatarUploadModalOpen(true)}
          ></img>

          {hasAvatarChanged && (
            <IconButton
              sx={{
                'position': 'absolute',
                'left': '50%',
                'top': 0,
                'transform': 'translate(-50%, 50%)',
                'p': 1,
                'minWidth': 0,
                '&:hover': {
                  bgcolor: 'custom.customDark',
                },
              }}
              onClick={() =>
                setCurrAvatar(new UploadedFileOrString(user.avatar || ''))
              }
            >
              <IconUndo
                sizeKey="24x24"
                extColor={theme.palette.primary.contrastText}
              ></IconUndo>
            </IconButton>
          )}

          {currAvatar.src && !hasAvatarChanged && (
            <IconButton
              sx={{
                'position': 'absolute',
                'left': '50%',
                'top': 0,
                'transform': 'translate(-50%, 50%)',
                'p': 1,
                'minWidth': 0,
                '&:hover': {
                  bgcolor: 'custom.customDark',
                },
              }}
              onClick={() => setCurrAvatar(new UploadedFileOrString(''))}
            >
              <IconTrash
                sizeKey="24x24"
                extColor={theme.palette.primary.contrastText}
              ></IconTrash>
            </IconButton>
          )}

          {currAvatar.src && (
            <>
              <IconButton
                sx={{
                  'position': 'absolute',
                  'left': '50%',
                  'transform': 'translate(-50%, -50%)',
                  'bottom': 0,
                  'p': 1,
                  'minWidth': 0,
                  '&:hover': {
                    bgcolor: 'custom.customDark',
                  },
                }}
                onClick={() => setIsAvatarEditModalOpen(true)}
              >
                <IconEdit
                  sizeKey="24x24"
                  extColor={theme.palette.primary.contrastText}
                ></IconEdit>
              </IconButton>
            </>
          )}
        </Box>
        <ActionInlineButton
          sx={{ my: sp.s60 }}
          sizeVariant="small"
          Icon={IconEdit}
          onClick={() => setIsAvatarUploadModalOpen(true)}
        >
          {t('profile.editImage')}
        </ActionInlineButton>
        {!isLessTablet && applyButtons}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: sp.s50,
          width: isLessLaptop ? 1 : 'auto',
        }}
      >
        <ProfileEditForm
          formikSubmited={formikSubmited}
          user={user}
          id={PROFILE_EDIT_FORM_ID}
          setHasProfileEditFormChanged={setHasProfileEditFormChanged}
          setIsLoading={setIsLoading}
          handleSubmit={handleEditSubmit}
          emailError={emailError}
          resetEmailError={() => setEmailError(null)}
          sx={{ ...(isLessLaptop ? { width: 1 } : {}) }}
        ></ProfileEditForm>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: sp.s20,
          }}
        >
          <Typography
            sx={{
              color: 'text.secondary',
            }}
            variant="f12"
          >
            Пароль
          </Typography>

          {isPasswordEdit ? (
            <PasswordEditForm
              handleGoToProfileEdit={() => setIsPasswordEdit(false)}
            ></PasswordEditForm>
          ) : (
            <ActionInlineButton
              sizeVariant="small"
              Icon={IconEdit}
              onClick={() => setIsPasswordEdit(true)}
            >
              {t('common.change')}
            </ActionInlineButton>
          )}
        </Box>
      </Box>

      {isLessTablet && applyButtons}

      {isAvatarEditModalOpen ? (
        <CroppModal
          isOpen={true}
          handleClose={() => setIsAvatarEditModalOpen(false)}
          src={currAvatar.src}
          onApply={onFileCropp}
        ></CroppModal>
      ) : (
        ''
      )}

      {isLessLaptop && isAvatarUploadModalOpen ? (
        uploadArea
      ) : (
        <DesktopSmallModal
          isOpen={isAvatarUploadModalOpen}
          handleClose={() => setIsAvatarUploadModalOpen(false)}
          disableCloseIcon={true}
        >
          {uploadArea}
        </DesktopSmallModal>
      )}
    </ProfileEditContainer>
  );
}
